import { useEffect, useRef } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";

/**
 * Reusable uPlot React wrapper. Owns the uPlot lifecycle (create/resize/destroy). Accessible by
 * default: the canvas is wrapped in a figure with role=img + aria-label and an sr-only textual
 * summary (a chart is never a bare canvas to a screen reader).
 */
export interface UPlotChartProps {
  options: Omit<uPlot.Options, "width" | "height">;
  data: uPlot.AlignedData;
  height?: number;
  ariaLabel: string;
  summary?: string;
  /** Receives the live plot on create and `null` on destroy.
   *
   *  Because the plot is no longer rebuilt when the options object changes (see
   *  `structureKey`), anything that must re-range or re-draw the LIVE instance —
   *  a zoom window, a cursor sync — needs the instance itself. */
  onPlot?: (plot: uPlot | null) => void;
}

type ChartOptions = Omit<uPlot.Options, "width" | "height">;

/**
 * A signature of the options' STRUCTURE, derived from content rather than object
 * identity.
 *
 * The plot used to be destroyed and rebuilt on every React render: the effect was
 * keyed on the `options` and `data` objects, and callers rebuild both every render
 * (`chartTheme()` returns a fresh literal, so the callers' `useMemo` never
 * memoises). That reset the cursor on every parent state change — a hover readout
 * vanished if anything above re-rendered — and it is why no interaction could hold
 * state: a drag-zoom would have been undone by the next render (QA, 25 Oct).
 *
 * Only a change of SHAPE needs a new uPlot instance (series added or renamed, an
 * axis or scale appearing). Everything else is applied in place. Keying on this
 * string means identical options rebuilt from scratch compare equal.
 */
function structureKey(o: ChartOptions): string {
  const series = (o.series ?? [])
    .map((s) => (s && typeof s === "object" ? (s as uPlot.Series).label ?? "" : ""))
    .join("|");
  const axes = (o.axes ?? [])
    .map((a) => (a && typeof a === "object" ? (a as uPlot.Axis).scale ?? "" : ""))
    .join("|");
  const scales = Object.keys(o.scales ?? {}).sort().join("|");
  return `${(o.series ?? []).length}::${series}::${axes}::${scales}`;
}

export function UPlotChart({
  options, data, height = 180, ariaLabel, summary, onPlot,
}: UPlotChartProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const plotRef = useRef<uPlot | null>(null);
  // Latest values for the creation effect, which must not re-run on identity churn.
  const optionsRef = useRef(options);
  const dataRef = useRef(data);
  const onPlotRef = useRef(onPlot);
  optionsRef.current = options;
  dataRef.current = data;
  onPlotRef.current = onPlot;

  const structure = structureKey(options);

  // Create once per structure (and per height). Not per render.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const width = host.clientWidth || 600;
    const plot = new uPlot({ ...optionsRef.current, width, height }, dataRef.current, host);
    plotRef.current = plot;
    onPlotRef.current?.(plot);
    const ro = new ResizeObserver(() => {
      plot.setSize({ width: host.clientWidth || width, height });
    });
    ro.observe(host);
    return () => {
      ro.disconnect();
      onPlotRef.current?.(null);
      plot.destroy();
      plotRef.current = null;
    };
  }, [structure, height]);

  // New numbers for the same shape are pushed into the live plot, so the instance —
  // and with it the cursor, the legend and any zoom — survives a data refresh.
  useEffect(() => {
    const plot = plotRef.current;
    if (plot && plot.data !== data) plot.setData(data);
  }, [data]);

  return (
    <figure role="group" aria-label={ariaLabel} className="m-0">
      <div ref={hostRef} role="img" aria-label={ariaLabel} />
      {summary ? <figcaption className="sr-only">{summary}</figcaption> : null}
    </figure>
  );
}
